// Эндпоинты ведомости: шаблоны колонок, реестр за период, сетка, массовое создание, сводный
// экран по человеку.
//
// Формы — зеркало contracts/openapi/bark-core.v1.yaml. Контракт правит сессия сервера; поле,
// придуманное на фронте, даёт зелёную сборку и пустой экран у человека.
//
// Шесть особенностей, из-за которых типы выглядят именно так.
//
// 1. **У колонки ДВА заголовка сразу.** `title` — то, что уходит в печать и в выгрузку, это
//    бланк заказчика. `catalog_title` — то же слово из каталога. В объектовой ведомости
//    заказчика «Официально получать» держит УЖЕ ВЫПЛАЧЕННОЕ, а «Официально выдано» — ОСТАТОК,
//    то есть обратно смыслу слов. Переименовать нельзя: печатается его бланк. Поэтому смысл
//    объясняется подсказкой рядом (`source_note` и подпись по КОДУ колонки), а заголовок
//    берётся с сервера и в вебе не пишется руками ни разу.
// 2. **Удержания висят на человеке, начисления — на строке.** У человека может быть две
//    тарифные строки (два поста, K5: 90 000/558 и 80 000/372), и удержание формы проставлено
//    ровно один раз, а не по разу на строку. Отсюда два `amounts`: `PayrollRowOut.amounts` и
//    `PayrollEmployeeOut.amounts`, и складывать их нельзя.
// 3. **`official_paid` и `official_rest` названы по СМЫСЛУ, а не по заголовку.** `paid` — уже
//    выплаченное в периоде, `rest` — остаток. Контроль заказчика: Шатун А., 50 000 плюс
//    12 764 равно 62 764.
// 4. **`calculated` отделён от нулей.** Ложь означает «расчёт ещё не проводился, суммы нули»,
//    а не «к выплате ноль». Слипание этих двух фактов в один прочерк — дефект: по такой
//    ячейке решают, доплачивать ли человеку.
// 5. **Ставка и часы — строки.** Ставка `dec(18,6)`, часы `dec(6,2)`, норматив `dec(8,1)`.
//    JSON-число в браузере становится приближением с плавающей точкой, а расчёт от округлённой
//    ставки даёт 84 816 вместо 85 000.
// 6. **Суммы — целые копейки.** Ни одной дробной денежной величины в ведомости нет.

import type { FileResponse } from '@/shared/api/client';
import { request, requestFile, type RequestOptions } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { ListResponse } from '@/shared/api/types';

// ── Замороженные наборы кодов ─────────────────────────────────────────────────────────────

/**
 * Поведение ячейки на сервере. Своё, не совпадающее с набором сетки: сервер говорит
 * `foreign`/`locked`, сетка — `linked`/`blocked`. Перевод делает каталог колонок, и делает его
 * ровно в одном месте.
 */
export const COLUMN_BEHAVIOURS = ['input', 'computed', 'foreign', 'locked'] as const;
export type ColumnBehaviour = (typeof COLUMN_BEHAVIOURS)[number];

/** Вид колонки: начисление, удержание, официальная часть, служебная, итог. */
export const COLUMN_KINDS = ['accrual', 'deduction', 'official_split', 'service', 'total'] as const;
export type ColumnKind = (typeof COLUMN_KINDS)[number];

/**
 * Состояния ведомости. Справочник целиком приезжает вместе с сеткой (`status_options`), и
 * рисовать надо ЕГО: этот набор нужен для отбора в адресе и для проверок.
 */
export const PAYROLL_STATUSES = ['draft', 'review', 'approved', 'paid', 'closed'] as const;
export type PayrollStatus = (typeof PAYROLL_STATUSES)[number];

export function isPayrollStatus(value: string): value is PayrollStatus {
  return (PAYROLL_STATUSES as readonly string[]).includes(value);
}

/** Тип тарифной строки: по часам либо оклад (административная ведомость). */
export const ROW_TYPES = ['hourly', 'salary'] as const;
export type RowType = (typeof ROW_TYPES)[number];

/**
 * Три законных режима тарифа. Ставка НИ В ОДНОМ из них не вводится числом, кроме `direct`.
 *
 * `by_period` — пара «сумма за период и норма часов»: 85 000 при норме 558 даёт 152,329749.
 * `by_target` — целевая сумма: «человек должен получить 74 516 за 348 часов». Только так
 * проводится взыскание у заказчика; обратный ход (ввести 214) даёт 74 472, то есть превращает
 * взыскание 5 000 в 5 044.
 * `direct` — фиксированная ставка за час. Существует потому, что у заказчика есть объекты со
 * ставкой «165 рублей в час», и только здесь ставка вводится числом.
 */
export const RATE_MODES = ['by_period', 'by_target', 'direct'] as const;
export type RateMode = (typeof RATE_MODES)[number];

/**
 * Коды колонок, значение которых лежит НЕ в `amounts`, а отдельным полем.
 *
 * Список нужен каталогу колонок: по нему он решает, откуда брать число. Держать его здесь, а
 * не в разметке, потому что это часть договора с сервером, а не оформление.
 */
export const COLUMN_HOURS = 'hours';
export const COLUMN_RATE = 'rate';
export const COLUMN_ACCRUED = 'accrued';
export const COLUMN_ADVANCE = 'advance';
export const COLUMN_OFFICIAL_PAID = 'official_paid';
export const COLUMN_OFFICIAL_REST = 'official_rest';
export const COLUMN_TOTAL = 'total';

// ── Итоги ─────────────────────────────────────────────────────────────────────────────────

/** Итоги ведомости. Ноль здесь означает «посчитано и ноль», а не «не знаем». */
export interface PayrollTotalsOut {
  employees_count: number;
  rows_count: number;
  /** Строк выведено из расчёта с основанием. */
  excluded_rows: number;
  /** Начислено по тарифным строкам, копейки. */
  accrued: number;
  /** Всего к выплате по людям, копейки. */
  total_payout: number;
}

// ── Ведомость ─────────────────────────────────────────────────────────────────────────────

/** Ведомость целиком: шапка, состояние, итоги. Объекта нет — административная. */
export interface PayrollOut {
  id: string;
  /** Пусто — ведомость аппарата управления. */
  site_id: string | null;
  site_name: string | null;
  /**
   * Город из карточки объекта: по нему реестр складывается в группы. Пусто — город не указан
   * либо ведомость аппарата управления. Необязательно: сервер до 11.10.2026 поля не присылал.
   */
  site_city?: string | null;
  legal_entity_id: string;
  legal_entity_name: string;
  period_year: number;
  period_month: number;
  /** Период словами: «август 2026». Собирает сервер. */
  period_title: string;
  /** Номер ведомости; проставляется при утверждении. */
  number: string | null;
  status: string;
  status_title: string;
  is_administrative: boolean;
  template_id: string | null;
  template_title: string | null;
  totals: PayrollTotalsOut;
  /**
   * Скольким людям расчёт правда проводился.
   *
   * Приходит С СЕРВЕРА и выводу в браузере не подлежит. До 17.09.2026 кабинет считал
   * «посчитана ли ведомость» как «итог не ноль», и ведомость, честно посчитанная в ноль,
   * показывалась как «ещё не считали»: человек жал «Пересчитать» на том, что уже посчитано.
   */
  calculated_employees: number;
  /** Скольких людей надо разобрать, прежде чем утверждать. */
  blocking_employees: number;
  rows_without_hours: number;
  rows_without_rate: number;
  /** Состояние табеля объекта за этот месяц. Пусто — табеля нет вовсе. */
  timesheet_status?: string | null;
  /** Одной фразой: почему здесь ноль и что с этим делать. Пусто — объяснять нечего. */
  zero_reason?: string | null;
  version: number;
  can_edit: boolean;
  blocked_reason?: string | null;
  /** Норматив часов объекта за период, строкой. */
  norm_hours?: string | null;
  /** Факт часов по табелю, строкой. */
  fact_hours?: string | null;
  /** Связанный официальный период: пусто — стыка ещё нет. */
  official_period_id?: string | null;
  prepared_at?: string | null;
  approved_at?: string | null;
  second_approved_at?: string | null;
  paid_at?: string | null;
}

/** Заведение одной ведомости. Объект указан — объектовая, не указан — административная. */
export interface PayrollIn {
  year: number;
  month: number;
  site_id?: string | null;
  /** Обязательно для административной; у объектовой берётся с объекта. */
  legal_entity_id?: string | null;
  /** Пусто — шаблон объекта, его типа либо по умолчанию. */
  payroll_template_id?: string | null;
  /** Строки-оклады. Только для административной ведомости. */
  salary_rows?: SalaryRowIn[];
}

export interface SalaryRowIn {
  employee_id: string;
  /** Сумма оклада в копейках. */
  amount: number;
  post_name?: string | null;
}

// ── Колонки ───────────────────────────────────────────────────────────────────────────────

/**
 * Колонка шаблона. Два заголовка сразу: печатный и смысловой.
 *
 * `title` уходит в печать — это бланк заказчика, и переименовать его нельзя. `is_overridden`
 * говорит, что бланк расходится с каталогом; сам разворот смысла в официальной паре виден не
 * по этому признаку, а по `source_note`, где он назван словами.
 */
export interface PayrollColumnOut {
  code: string;
  /** Заголовок заказчика: именно он уходит в печать и выгрузку. */
  title: string;
  /** Тот же заголовок из каталога. */
  catalog_title: string;
  /** Заголовок задан шаблоном заказчика и расходится с каталогом. */
  is_overridden: boolean;
  /**
   * Заголовок ИМЕННО этого шаблона, как его задали. Пусто — берётся каталожный.
   *
   * Отдельно от `title` затем, чтобы поле настройки было пустым, когда своего заголовка нет:
   * подставленный туда каталожный превращает наследование в жёстко вписанное слово при
   * первом же сохранении.
   */
  title_override: string | null;
  kind: string;
  kind_title: string;
  behaviour: string;
  behaviour_hint: string | null;
  /** Почему править нельзя. Есть у `locked`. */
  lock_reason: string | null;
  /** Откуда берётся значение, словами. Здесь же сказано про перевёрнутые заголовки. */
  source_note: string | null;
  requires_basis: boolean;
  /** График удержания по умолчанию, копейки. */
  schedule_default: number | null;
  sort_order: number;
  is_visible: boolean;
}

/** Шаблон ведомости: набор и порядок колонок. */
export interface PayrollTemplateOut {
  id: string;
  code: string;
  title: string;
  is_administrative: boolean;
  is_default: boolean;
  is_active: boolean;
  version: number;
  columns?: PayrollColumnOut[];
  columns_count: number;
  /** Скольким объектам шаблон задан явно. */
  sites_count: number;
  /** Скольким типам объектов шаблон задан по умолчанию. */
  site_types_count: number;
}

/** Шаблон коротко: то, что едет вместе с сеткой. */
export interface TemplateBriefOut {
  id: string;
  code: string;
  title: string;
  is_administrative: boolean;
}

export interface TemplateColumnIn {
  column_code: string;
  is_visible?: boolean;
  /** Заголовок заказчика. Пусто — берётся заголовок каталога. */
  title_override?: string | null;
}

export interface TemplateColumnsIn {
  columns: TemplateColumnIn[];
  /** Версия шаблона; передана — правка отклоняется, если шаблон успели изменить. */
  version?: number | null;
}

// ── Строки и люди ─────────────────────────────────────────────────────────────────────────

/** Тарифная строка. Удержаний здесь нет: они висят на сотруднике. */
export interface PayrollRowOut {
  id: string;
  assignment_id: string | null;
  post_name: string | null;
  row_type: string;
  row_type_title: string;
  /** Факт часов, строкой. */
  hours: string | null;
  /** Ставка, шесть знаков, строкой. Округление до рубля — показ, не расчёт. */
  rate: string | null;
  /** Сумма за период, копейки: исходное значение расчёта. */
  period_amount: number | null;
  /** Норма часов, строкой. */
  norm_hours: string | null;
  /** Начислено по строке, копейки. */
  accrued: number;
  /** Начислено до понижения тарифа. Отличается от `accrued` только при взыскании. */
  accrued_before_penalty?: number | null;
  /** Режим тарифа ЭТОЙ строки: снят с назначения при создании и дальше живёт своей жизнью. */
  rate_mode?: string | null;
  rate_mode_title?: string | null;
  /** Целевая сумма при взыскании, копейки. */
  target_amount?: number | null;
  /** Основание понижения тарифа в этой строке. */
  rate_change_basis?: string | null;
  /** Ставка, округлённая до рубля сервером. Показываем свою; это для сверки. */
  rate_displayed?: number | null;
  is_excluded: boolean;
  exclusion_basis: string | null;
  /** Начисления по колонкам, копейки. Ключ — код колонки. */
  amounts?: Record<string, number>;
  sort_order: number;
  version: number;
}

/** Расчёт по сотруднику. Каскад удержаний применяется здесь и ровно один раз. */
export interface PayrollEmployeeOut {
  id: string;
  employee_id: string;
  employee_name: string;
  employee_status: string | null;
  /** Начислено всего по тарифным строкам человека, копейки. */
  accruals_total: number;
  /** Официально ВЫПЛАЧЕНО в периоде, копейки. Смысл, а не заголовок бланка. Пусто — скрыто ролью. */
  official_paid: number | null;
  /** Официальный ОСТАТОК к выплате, копейки. Смысл, а не заголовок бланка. Пусто — скрыто ролью. */
  official_rest: number | null;
  /** База после вычета официальной части — шаг 2 каскада. */
  unofficial_base: number;
  /** Удержано авансов, копейки. */
  advance_total: number;
  /** Остаток после аванса — шаг 4. Отрицательный означает нехватку начисленного. */
  after_advance: number;
  total_payout: number;
  /** Итог обнулён каскадом: не хватило начисленного на аванс. Это НЕ обычный ноль. */
  flag_zeroed_by_cascade: boolean;
  /** Человека нет в связанном официальном периоде: официальная часть подставлена нулями. */
  no_official_row: boolean;
  flags: string[];
  /** Что показать в панели проверки, человеческим языком. */
  review_notes: string[];
  /**
   * Шаги расчёта человеческим языком.
   *
   * Заполняется ОТВЕТОМ НА ПЕРЕСЧЁТ этого человека; в сетке ведомости пусто, иначе один экран
   * вёз бы 281 объяснение. Из 85 000 у охранника остаётся 33 131, и если не показать, куда
   * делось остальное, он идёт выяснять к старшему, а старший — в финотдел.
   */
  explain?: ExplainStepOut[];
  /** Удержания и внетарифные начисления, копейки. Ключ — код колонки. */
  amounts?: Record<string, number>;
  rows?: PayrollRowOut[];
  /**
   * Разовые удержания по видам долга в этой ведомости: код колонки → сумма и основание.
   * Пусто — по всем видам удерживается по графику. Необязательное: старый сервер его не шлёт.
   */
  deduction_overrides?: Record<string, DeductionOverrideOut>;
  calculated_at: string | null;
  /**
   * Последние четыре цифры карты либо телефона из ДЕЙСТВУЮЩЕГО заявления о реквизитах.
   *
   * Сценарий 76: «в строке ведомости показаны последние четыре цифры номера и дата». Номера
   * целиком нет ни здесь, ни в печати.
   */
  /** Действующая отметка о выплате: по ней оформляется сторно. Пусто — отметки нет либо её сторнировали. */
  payout_fact_id: string | null;
  payout_last4: string | null;
  payout_method: string | null;
  /** Когда, сколько и чем выплачено. Пусто — выплата не отмечена. */
  paid_at: string | null;
  paid_amount: number | null;
  paid_method: string | null;
  /**
   * Сумма ВСЕХ действующих отметок о выплате по этому расчёту, копейки. Ноль — выплат нет.
   *
   * ⚠️ Не то же, что `paid_amount`: тот несёт сумму только ПОСЛЕДНЕЙ отметки. Человеку бывает
   * переведено в два-три приёма, и сколько ему ещё должны, видно только по сумме всех; разницу
   * «итог минус последняя отметка» считать нельзя, она соврёт на каждой частичной выплате.
   */
  paid_total: number;
  version: number;
  /**
   * Другие ведомости человека в этом месяце и юрлице, видимые смотрящему.
   *
   * Форма по графику, аванс и официальная часть берутся один раз на месяц человека и лежат в
   * ОДНОЙ из его ведомостей; в остальных по ним ноль. Отсюда подсказка у нулевой ячейки и блок
   * «Этот месяц в других ведомостях» в панели человека. Пусто — других ведомостей нет либо
   * смотрящему они не видны. Необязательное: сервер до 19.10.2026 его не присылал.
   */
  month_siblings?: MonthSiblingOut[];
  /**
   * У человека в этом месяце есть ведомости, которые смотрящему не видны. Только признак: ни
   * числа, ни названий, ни сумм. Итог месяца в панели тогда называется итогом по видимым.
   * Необязательное: сервер до 20.10.2026 его не присылал.
   */
  month_siblings_hidden?: boolean;
}

/**
 * Другая ведомость человека в том же месяце и юрлице.
 *
 * Название и суммы соседа живут только здесь: в шагах объяснения сервер их не пишет, шаги видит
 * каждый, кто видит ЭТУ ведомость, а соседняя ему может быть закрыта.
 */
export interface MonthSiblingOut {
  payroll_id: string;
  /** Расчёт человека в той ведомости: по нему открывается его панель там. */
  payroll_employee_id: string;
  /** Объект ведомости. Пусто — аппарат управления. */
  site_name: string | null;
  status: string;
  status_title: string;
  accruals_total: number;
  /** Пусто — официальная часть скрыта ролью. */
  official_paid: number | null;
  official_rest: number | null;
  /** Аванс месяца, удержанный в той ведомости. */
  advance_total: number;
  /**
   * Удержания статей месяца в той ведомости по кодам колонок, копейки: долги по графику и
   * разом, аванс. Начислений, введённого руками и долгов процентом от выплаты здесь нет.
   */
  amounts: Record<string, number>;
  total_payout: number;
  calculated_at: string | null;
}

/** Один шаг объяснения расчёта: что вычли, сколько и сколько осталось. */
export interface ExplainStepOut {
  step: number;
  code: string;
  title: string;
  text: string;
  /** Сумма шага, копейки. Пусто — шаг без суммы. */
  amount?: number | null;
  /** Сколько осталось после шага, копейки. */
  running_total?: number | null;
  /**
   * Куда сумма двигает итог: `accrual`, `deduction`, `total` либо `info` (не двигает).
   *
   * ⚠️ Приходит С СЕРВЕРА и не выводится из кода шага: коды долговых шагов собираются из вида
   * долга на лету, и полного списка здесь не будет никогда.
   */
  direction?: string;
}

/**
 * Всё, что нужно экрану ведомости, одним ответом.
 *
 * Одним, а не тремя: счётчики полосы проверки, посчитанные отдельным запросом, разойдутся с
 * сеткой на любой параллельной правке — полоса скажет «три замечания», а в сетке их окажется
 * два, и человек перестанет верить обеим.
 */
export interface PayrollGridOut {
  payroll: PayrollOut;
  template: TemplateBriefOut | null;
  columns?: PayrollColumnOut[];
  employees?: PayrollEmployeeOut[];
  totals: PayrollTotalsOut;
  /** Расчёт уже проводился. Ложь означает «суммы ещё нули», а не «ноль к выплате». */
  calculated: boolean;
  /** Справочник состояний: код → слово. */
  status_options?: Record<string, string>;
}

// ── Правка и пересчёт ─────────────────────────────────────────────────────────────────────

/**
 * Одна ячейка, введённая с клавиатуры.
 *
 * Принимаются ТОЛЬКО колонки с поведением «вводится»: премия, прочие премии, штраф. Всё
 * остальное считается системой либо приходит из другой её части, и ввод туда означал бы два
 * числа одного смысла из разных источников.
 */
export interface CellIn {
  column_code: string;
  /** Копейки. Отрицательных сумм в ведомости не бывает. */
  amount: number;
  /** Основание. Обязательно у колонок, которые его требуют, например у штрафа. */
  basis?: string | null;
}

/**
 * Пачка ячеек ОДНОГО человека: пересчёт после записи идёт по нему целиком.
 *
 * Единица пересчёта — сотрудник, а не ячейка: каскад удержаний применяется к сумме начислений
 * всех его тарифных строк ровно один раз. У Ванина Н. две строки, и пересчёт по строке удержал
 * бы фиксированные долги дважды.
 */
export interface CellsIn {
  cells: CellIn[];
  /** Версия расчёта. Передана — правка отклоняется, если его успели изменить. */
  version?: number | null;
}

/** Разовое удержание по виду долга в этой ведомости вместо графика (тз 5.5). */
export interface DeductionOverrideOut {
  /** Сколько удержать в этой ведомости по этому виду, копейки. */
  amount: number;
  /** Почему сумма отличается от графика. */
  basis: string;
}

/**
 * Разовое удержание по виду долга в этой ведомости. Со следующего месяца долг снова гасится
 * по своему графику сам: решение привязано к расчёту в ведомости, а не к долгу.
 */
export interface DeductionOverrideIn {
  /** Копейки. Пусто — вернуть по графику, ноль — не удерживать в этой ведомости. */
  amount: number | null;
  /** Почему сумма отличается от графика. Обязательно: это денежное решение. */
  basis: string;
  version?: number | null;
}

/** Правка строки: часы, сумма оклада, вывод из расчёта. Тариф правится отдельно. */
export interface PayrollRowPatchIn {
  /** Факт часов строкой: «558.00». Число в json теряет точность. */
  hours?: string | null;
  /** Сумма оклада, копейки. Только для строки-оклада. */
  accrued?: number | null;
  is_excluded?: boolean | null;
  /** Почему строка выведена из расчёта. Без основания вывод не сохраняется. */
  exclusion_basis?: string | null;
  version?: number | null;
}

/**
 * Изменение тарифа в строке ведомости. Основание ОБЯЗАТЕЛЬНО.
 *
 * Тариф назначения при этом не меняется: взыскание разовое, за этот месяц. Изменив назначение,
 * мы понизили бы человеку тариф и на следующий месяц тоже, и он недополучил бы второй раз без
 * единого следа.
 */
export interface RowRateIn {
  /** Основание, обязательно. */
  basis: string;
  rate_mode?: RateMode | null;
  /** Сумма за период, копейки. Режим «пара сумма и норма». */
  period_amount?: number | null;
  /** Норма часов строкой: «558.0». */
  norm_hours?: string | null;
  /** Целевая сумма, копейки: сколько человек должен получить. Режим взыскания. */
  target_amount?: number | null;
  /** Только для режима фиксированной ставки за час. */
  hourly_rate?: string | null;
  version?: number | null;
}

/** Новая строка ведомости. Тарифа здесь нет: он снимается с назначения. */
export interface PayrollRowIn {
  employee_id: string;
  row_type?: RowType;
  /** Откуда взять тариф. Пусто — только для строки-оклада. */
  assignment_id?: string | null;
  hours?: string | null;
  /** Сумма оклада, копейки. Только для строки-оклада. */
  accrued?: number | null;
  sort_order?: number | null;
}

/** Кто и почему попал в панель проверки после пересчёта. */
export interface ReviewNoteOut {
  employee_id: string;
  employee_name: string;
  notes?: string[];
}

/**
 * Отчёт о пересчёте ведомости — СВОДКОЙ, а не сеткой.
 *
 * Человек, нажавший «пересчитать», спрашивает «что изменилось», а не «покажи всё заново»:
 * сетку он и так держит открытой.
 */
export interface RecalcOut {
  payroll_id: string;
  employees: number;
  rows: number;
  accrued: number;
  total_payout: number;
  /** Версия набора настроек расчёта: по ней объясняются числа через полгода. */
  config_version: string;
  summary: string;
  review?: ReviewNoteOut[];
  /**
   * Скольким людям пересчитаны их ДРУГИЕ ведомости этого месяца: месяц человека на нескольких
   * объектах считается целиком. Необязательное: сервер до 19.10.2026 его не присылал.
   */
  siblings_recalculated?: number;
}

// ── Массовое создание ─────────────────────────────────────────────────────────────────────

export interface GenerateIn {
  year: number;
  month: number;
  /** Пусто — все действующие объекты периода. */
  site_ids?: string[];
  legal_entity_id?: string | null;
}

/** Что вышло по одному объекту. Пропуск без причины читается как сбой. */
export interface GenerateSiteOut {
  site_id: string;
  site_name: string;
  payroll_id: string | null;
  created: boolean;
  employees: number;
  rows: number;
  norm_hours: string | null;
  template_title: string | null;
  /** Почему объект пропущен. */
  reason: string | null;
}

export interface GenerateOut {
  year: number;
  month: number;
  created: number;
  skipped: number;
  employees: number;
  rows: number;
  /** Повтор по ключу операции: вернули прежний результат, а не вторую пачку ведомостей. */
  repeated: boolean;
  summary: string;
  sites?: GenerateSiteOut[];
}

/** Пересчитать все ведомости периода разом. */
export interface RecalcPeriodIn {
  year: number;
  month: number;
  /** Пусто — все ведомости периода. */
  site_ids?: string[];
  legal_entity_id?: string | null;
}

/**
 * Одна причина, по которой в периоде остались нули.
 *
 * Ради этого списка всё и делается: человеку, нажавшему одну кнопку на семьдесят семь
 * ведомостей, нужен не счётчик пересчитанных, а ответ «чего не хватает и скольким».
 */
export interface RecalcReasonOut {
  code: string;
  people: number;
  rows: number;
  /** Утверждать нельзя, пока это не разобрано. */
  blocking: boolean;
  text: string;
  /** Коротким глаголом: «заполнить тариф», «открыть табель». */
  next_action: string;
}

export interface RecalcSheetOut {
  payroll_id: string;
  site_name: string;
  status: string;
  recalculated: boolean;
  employees: number;
  accrued: number;
  total_payout: number;
  blocking_employees: number;
  /** Почему пропущена. Пропуск без причины читается как сбой. */
  reason: string | null;
}

export interface RecalcPeriodOut {
  year: number;
  month: number;
  payrolls: number;
  recalculated: number;
  skipped: number;
  employees: number;
  sheets_with_money: number;
  accrued: number;
  total_payout: number;
  /** Скольким строкам тариф дозаполнен из назначения: это правка данных, а не счёт. */
  rate_pulled: number;
  /**
   * Скольким строкам снятый тариф заменён действовавшим в месяце ведомости: тариф назначения
   * поменяли с первого числа месяца или раньше уже после создания ведомости. Необязательно:
   * сервер до 13.10.2026 поля не присылал.
   */
  rate_refreshed?: number;
  blocking_employees: number;
  reasons: RecalcReasonOut[];
  sheets: RecalcSheetOut[];
  summary: string;
}

// ── Шаблон объекта ────────────────────────────────────────────────────────────────────────

export interface SiteTemplateIn {
  /** Пусто — объект возвращается к шаблону своего типа. */
  payroll_template_id?: string | null;
  version?: number | null;
}

export interface SiteTemplateOut {
  site_id: string;
  site_name: string;
  payroll_template_id: string | null;
  template_code: string | null;
  template_title: string | null;
  /** site, site_type или default: откуда взялся шаблон. */
  source?: string | null;
  /** То же словами: человеку важно, поменяется ли это у соседей. */
  source_title?: string | null;
  version: number;
}

// ── Сводный экран по человеку ─────────────────────────────────────────────────────────────

/** Одна ведомость человека коротко. */
export interface EmployeePayrollBriefOut {
  payroll_id: string;
  site_id: string | null;
  site_name: string | null;
  period_year: number;
  period_month: number;
  period_title: string;
  status: string;
  status_title: string;
  number: string | null;
  accrued: number;
  total_payout: number;
  rows_count: number;
}

/** Расчёты человека внутри одного юрлица. Между юрлицами суммы НЕ складываются. */
export interface EmployeeEntityOut {
  legal_entity_id: string;
  legal_entity_name: string;
  accrued: number;
  total_payout: number;
  payrolls: EmployeePayrollBriefOut[];
}

export interface EmployeePayrollsOut {
  employee_id: string;
  employee_name: string;
  employee_status: string | null;
  period_year: number | null;
  period_month: number | null;
  entities?: EmployeeEntityOut[];
  entities_count: number;
  /** Почему общего итога нет, если юрлиц больше одного. */
  note: string | null;
}

// ── Запросы ───────────────────────────────────────────────────────────────────────────────

export interface RegistryFilter {
  year?: number;
  month?: number;
  status?: string;
  legal_entity_id?: string;
  site_id?: string;
  /** Заказчик: у ведомости он не хранится, отбор идёт через объекты этого заказчика. */
  customer_id?: string;
  /** Только ведомости аппарата управления. */
  administrative_only?: boolean;
}

export function fetchRegistry(
  params: RegistryFilter = {},
  options?: RequestOptions,
): Promise<ListResponse<PayrollOut>> {
  return request<ListResponse<PayrollOut>>(`/api/v1/payroll${queryString({ ...params })}`, options);
}

export function fetchGrid(payrollId: string, options?: RequestOptions): Promise<PayrollGridOut> {
  return request<PayrollGridOut>(`/api/v1/payroll/${payrollId}`, options);
}

export function fetchTemplates(
  options?: RequestOptions,
): Promise<ListResponse<PayrollTemplateOut>> {
  return request<ListResponse<PayrollTemplateOut>>('/api/v1/payroll/templates', options);
}

/**
 * Один шаблон по опознавателю. Вызовов в вебе нет НАМЕРЕННО, это не дыра: настройка шаблона
 * (вкладка «Ведомость» объекта, сценарий 22) работает от списка `fetchTemplates` — список и
 * так в кэше ради селектора, и отдельный запрос за одним шаблоном был бы вторым запросом за
 * тем же ответом. Дверь остаётся в контракте для точечного чтения без списка.
 * Сверено с тз 26.08.2026 при проверке «функция клиента, которую никто не зовёт».
 */
export function fetchTemplate(
  templateId: string,
  options?: RequestOptions,
): Promise<PayrollTemplateOut> {
  return request<PayrollTemplateOut>(`/api/v1/payroll/templates/${templateId}`, options);
}

export function fetchEmployeeCard(
  employeeId: string,
  params: { year?: number; month?: number } = {},
  options?: RequestOptions,
): Promise<EmployeePayrollsOut> {
  return request<EmployeePayrollsOut>(
    `/api/v1/payroll/employees/${employeeId}${queryString({ ...params })}`,
    options,
  );
}

// ── Действия ──────────────────────────────────────────────────────────────────────────────

export function createPayroll(body: PayrollIn): Promise<PayrollOut> {
  return request<PayrollOut>('/api/v1/payroll', { method: 'POST', body });
}

/**
 * Массовое создание ведомостей на период.
 *
 * Ключ операции обязателен: повтор возвращает ПРЕЖНИЙ отчёт, а не вторую пачку ведомостей.
 * Ключ живёт столько же, сколько форма мастера, — иначе двойное нажатие заведёт всё дважды.
 */
export function generatePayrolls(body: GenerateIn, idempotencyKey: string): Promise<GenerateOut> {
  return request<GenerateOut>('/api/v1/payroll/generate', {
    method: 'POST',
    body,
    idempotencyKey,
  });
}

/**
 * Пересчёт всего периода.
 *
 * Ключа операции нет намеренно: пересчёт денежных записей не рождает, он обновляет мнение о
 * суммах по нынешним данным. Повтор осмыслен — заполнил тариф и жмёшь снова.
 */
export function recalculatePeriod(body: RecalcPeriodIn): Promise<RecalcPeriodOut> {
  return request<RecalcPeriodOut>('/api/v1/payroll/recalculate-period', {
    method: 'POST',
    body,
  });
}

export function setTemplateColumns(
  templateId: string,
  body: TemplateColumnsIn,
): Promise<PayrollTemplateOut> {
  return request<PayrollTemplateOut>(`/api/v1/payroll/templates/${templateId}/columns`, {
    method: 'PUT',
    body,
  });
}

/**
 * Ведомость файлом: как на экране либо печатной формой под подпись.
 *
 * Печатная форма — это не «то же самое, но красивее»: сервер добавляет в неё колонки «Дата» и
 * «Подпись», ставит альбомный лист и шапку с реквизитами юрлица. Именно её печатают и
 * подписывают у людей, и сценарий приёмки 40 требует в ней ровно эти две колонки.
 *
 * До 26.08.2026 клиентской функции к этой двери не было вовсе: ведомость — главный документ
 * продукта — нельзя было ни выгрузить, ни напечатать из программы, хотя сервер умел и то, и
 * другое и был закрыт зелёным тестом.
 */
export function exportPayroll(
  payrollId: string,
  form: 'sheet' | 'signing' = 'sheet',
): Promise<FileResponse> {
  return requestFile(`/api/v1/payroll/${encodeURIComponent(payrollId)}/export?form=${form}`);
}

export function setSiteTemplate(siteId: string, body: SiteTemplateIn): Promise<SiteTemplateOut> {
  return request<SiteTemplateOut>(`/api/v1/payroll/sites/${siteId}/template`, {
    method: 'PUT',
    body,
  });
}

/**
 * Ячейки, вводимые с клавиатуры, и пересчёт ЭТОГО человека.
 *
 * Ответ — сам человек целиком, уже пересчитанный: сетку после правки перезапрашивать не надо,
 * а вот итоги ведомости надо, и это делает сброс в слое запросов.
 */
export function setCells(
  payrollId: string,
  employeeId: string,
  body: CellsIn,
): Promise<PayrollEmployeeOut> {
  return request<PayrollEmployeeOut>(`/api/v1/payroll/${payrollId}/employees/${employeeId}/cells`, {
    method: 'PUT',
    body,
  });
}

/** Разовое удержание по виду долга в этой ведомости и пересчёт этого человека. */
export function setDeductionOverride(
  payrollId: string,
  employeeId: string,
  columnCode: string,
  body: DeductionOverrideIn,
): Promise<PayrollEmployeeOut> {
  return request<PayrollEmployeeOut>(
    `/api/v1/payroll/${payrollId}/employees/${employeeId}/deductions/${encodeURIComponent(columnCode)}`,
    { method: 'PUT', body },
  );
}

/** Пересчёт всей ведомости: одна запись в журнал, а не по одной на человека. */
export function recalculate(payrollId: string): Promise<RecalcOut> {
  return request<RecalcOut>(`/api/v1/payroll/${payrollId}/recalculate`, { method: 'POST' });
}

/** Часы, сумма оклада, вывод строки из расчёта с основанием. */
export function updateRow(rowId: string, body: PayrollRowPatchIn): Promise<PayrollRowOut> {
  return request<PayrollRowOut>(`/api/v1/payroll/rows/${rowId}`, { method: 'PUT', body });
}

/** Тариф строки: основание обязательно, поля ввода самой ставки нет. */
export function setRowRate(rowId: string, body: RowRateIn): Promise<PayrollRowOut> {
  return request<PayrollRowOut>(`/api/v1/payroll/rows/${rowId}/rate`, { method: 'PUT', body });
}

/** Новая тарифная строка человека в ведомости. */
export function addRow(payrollId: string, body: PayrollRowIn): Promise<PayrollRowOut> {
  return request<PayrollRowOut>(`/api/v1/payroll/${payrollId}/rows`, { method: 'POST', body });
}

// ── Жизненный цикл ведомости ──────────────────────────────────────────────────────────────
//
// Четыре перехода — это те самые четыре нажатия, за которые закрывается месяц. Пересчёт в
// цепочку не входит: отправка и утверждение пересчитывают сами, а утверждать непересчитанное
// всё равно нельзя — значит это не отдельное решение человека.

export interface LifecycleIn {
  /** Версия ведомости с экрана: защита от чужой правки. */
  version?: number | null;
  /**
   * Итог к выплате, который человек видел перед нажатием, копейки. Расхождение останавливает
   * утверждение и называет новое число — вместо того чтобы молча утвердить другое.
   */
  expected_total_payout?: number | null;
  /**
   * Только для отметки выплаты: день перевода «2026-10-11». Пусто — сегодня. Тем, у кого
   * отметки ещё нет, встанет отметка на весь итог этим днём.
   */
  paid_on?: string | null;
}

export interface ReturnToWorkIn {
  /** Что именно оказалось неверно. Возврат отменяет погашения долгов и перенос аванса. */
  basis: string;
}

/** Пересчитать и отправить на согласование. */
export function submitPayroll(payrollId: string, body: LifecycleIn = {}): Promise<PayrollOut> {
  return request<PayrollOut>(`/api/v1/payroll/${payrollId}/submit`, { method: 'POST', body });
}

/** Второй уровень согласования: премии и изменения тарифов. */
export function secondApprovePayroll(
  payrollId: string,
  body: LifecycleIn = {},
): Promise<PayrollOut> {
  return request<PayrollOut>(`/api/v1/payroll/${payrollId}/second-approve`, {
    method: 'POST',
    body,
  });
}

/** Утвердить: номер, снимок условий, проводка денег. */
export function approvePayroll(payrollId: string, body: LifecycleIn = {}): Promise<PayrollOut> {
  return request<PayrollOut>(`/api/v1/payroll/${payrollId}/approve`, { method: 'POST', body });
}

/** Отметить, что деньги по ведомости выданы. */
export function markPayrollPaid(payrollId: string, body: LifecycleIn = {}): Promise<PayrollOut> {
  return request<PayrollOut>(`/api/v1/payroll/${payrollId}/mark-paid`, { method: 'POST', body });
}

/** Вернуть утверждённую ведомость в работу со сторно денежных записей. */
export function returnPayrollToWork(payrollId: string, body: ReturnToWorkIn): Promise<PayrollOut> {
  return request<PayrollOut>(`/api/v1/payroll/${payrollId}/return-to-work`, {
    method: 'POST',
    body,
  });
}
